// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo, type ReactNode, useState, useCallback, useLayoutEffect } from 'react';

import { type FacilityId, useEvolveGet, type ProjectId } from '@evolvemep/api-ts';

import { useFacilityOptional } from 'components/Mantine/GlobalFilters/FacilityRequired';
import { useProjectOptional } from 'components/Mantine/GlobalFilters/ProjectRequired';
import {
  type GlobalFiltersConfig,
  type GlobalFilterValues,
  globalFilterDefaults,
} from 'components/Mantine/GlobalFilters/types';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { strongKeys, strongIsEqual } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useLocalStorage } from 'hooks/useLocalStorage';

type GlobalFiltersContextType<ActiveAll = unknown> = {
  globalFilters: GlobalFilterValues<ActiveAll>;
  updateGlobalFilters: (filters: Partial<GlobalFilterValues<ActiveAll>>) => void;
  filterConfig: GlobalFiltersConfig<ActiveAll> | undefined;
  setFilterConfig: (config: GlobalFiltersConfig<ActiveAll> | undefined) => void;
};

const GlobalFiltersContext = React.createContext<GlobalFiltersContextType | undefined>(undefined);
export const GlobalFiltersProvider = ({ children }: { children: ReactNode }) => {
  const [globalFilters, setGlobalFilters] = useLocalStorage('GLOBAL_FILTERS', globalFilterDefaults);
  // The active filter config (if any) for the current page,
  // set via the `useGlobalFilters` hook.
  const [filterConfig, setFilterConfigInternal] = useState<GlobalFiltersContextType['filterConfig']>(undefined);

  const safeGlobalFilters = useMemo<typeof globalFilters>(() => {
    const keys = strongKeys(globalFilters).toSorted();
    const cachedKeys = strongKeys(globalFilterDefaults).toSorted();
    // If we've added a new filter, make sure it has a default value,
    // or we may fail when trying to access the user's cached value.
    const withDefaults = strongIsEqual(keys, cachedKeys)
      ? globalFilters
      : {
          ...globalFilterDefaults,
          ...globalFilters,
        };
    // TODO(FWP-1728): remove after 2026-12-01 — two months past deploy, by which point every session
    // predating the move has expired (JWTs live for one month). Drops facility/project selections cached
    // before projects/facilities moved to the shop API (old shape had `assignees`/`address`, no `members`);
    // the grouping in FacilitiesFilter/ProjectsFilter dereferences `members` on every selected item.
    const facilities = withDefaults.facilities.filter((facility) => 'members' in facility);
    const projects = withDefaults.projects.filter((project) => 'members' in project);
    if (facilities.length === withDefaults.facilities.length && projects.length === withDefaults.projects.length) {
      return withDefaults;
    }
    return { ...withDefaults, facilities, projects };
  }, [globalFilters]);

  const updateGlobalFilters = useCallback<GlobalFiltersContextType['updateGlobalFilters']>(
    (filters) => {
      setGlobalFilters((prev) => ({
        ...prev,
        ...filters,
      }));
    },
    [setGlobalFilters],
  );

  const setFilterConfig = useCallback<GlobalFiltersContextType['setFilterConfig']>(
    (config) => {
      if (isNotNil(config?.activeAll) && config.activeAll.key !== safeGlobalFilters.activeAll.key) {
        const state = config.activeAll.defaultState ?? globalFilterDefaults.activeAll.state;
        updateGlobalFilters({
          activeAll: {
            key: config.activeAll.key,
            state,
            value: (state === 'all' ? config.activeAll.allPossible : config.activeAll.whenActive) ?? undefined,
          },
        });
      }
      if (isNotNil(config?.timeFrame)) {
        const timeFrameOptions =
          config.timeFrame.groups?.flatMap((opt) => (opt.disabled ? [] : opt.items)) ?? config.timeFrame.options;
        if (
          isNil(safeGlobalFilters.timeFrame.timeFrame) ||
          (isNotNil(timeFrameOptions) && !timeFrameOptions.includes(safeGlobalFilters.timeFrame.timeFrame))
        ) {
          updateGlobalFilters({
            timeFrame: {
              timeFrame: config.timeFrame.defaultTimeFrame,
              date: null,
              dateRange: null,
            },
          });
        }
      }
      if (isNotNil(config?.workOrderStates)) {
        const validStates = safeGlobalFilters.workOrderStates.filter((s) => config.workOrderStates?.includes(s.name));
        if (
          !strongIsEqual(
            validStates.toSorted((a, b) => a.name.localeCompare(b.name)),
            safeGlobalFilters.workOrderStates.toSorted((a, b) => a.name.localeCompare(b.name)),
          )
        ) {
          updateGlobalFilters({
            workOrderStates: validStates,
          });
        }
      }
      setFilterConfigInternal(config);
    },
    [
      safeGlobalFilters.activeAll.key,
      safeGlobalFilters.workOrderStates,
      safeGlobalFilters.timeFrame.timeFrame,
      updateGlobalFilters,
    ],
  );

  const value = useMemo<GlobalFiltersContextType>(
    () => ({
      globalFilters: safeGlobalFilters,
      updateGlobalFilters,
      filterConfig,
      setFilterConfig,
    }),
    [filterConfig, safeGlobalFilters, setFilterConfig, updateGlobalFilters],
  );

  return <GlobalFiltersContext value={value}>{children}</GlobalFiltersContext>;
};

/**
 * Used to **define** and **access** global filters on a page.
 *
 * @remarks This hook should only be called _once per page_. When you call this hook, the available filters will be redefined.
 * */
export const useGlobalFilters = <ActiveAll = never,>(
  initialConfig: RequireAtLeastOne<GlobalFiltersConfig<ActiveAll>>,
) => {
  const context = useGeneralContext(GlobalFiltersContext, 'GlobalFilters');
  const { globalFilters, setFilterConfig } = context as GlobalFiltersContextType<ActiveAll>;
  const [config] = useState(initialConfig);

  useLayoutEffect(() => {
    setFilterConfig(config);
    return () => setFilterConfig(undefined);
  }, [config, setFilterConfig]);

  const filters = useMemo(
    () => ({
      facilityIds: globalFilters.facilities.map((f) => f.facilityId),
      projectIds: globalFilters.projects.map((p) => p.projectId),
      workOrderIds: globalFilters.workOrders.map((wo) => wo.workOrderId),
      assigneeIds: globalFilters.assignees.map((a) => a.userId),
      workCellIds: globalFilters.workCells.map((wc) => wc.workCellId),
      workOrderStateIds: globalFilters.workOrderStates.map((s) => s.stateId),
    }),
    [globalFilters],
  );

  return {
    ...filters,
    ...(globalFilters as GlobalFilterValues<ActiveAll>),
  };
};

/**
 * Used to update the global filters on a page.
 *
 * @remarks Generally, **you should not call this hook** – it is intended for internal use by global filter components.
 * */
export const useUpdateGlobalFilters = () => {
  const {
    setFilterConfig: _,
    globalFilters,
    updateGlobalFilters,
    ...rest
  } = useGeneralContext(GlobalFiltersContext, 'GlobalFilters');

  const facilityRequired = isNotNil(useFacilityOptional());
  const { apiCall: getFacilityApiCall } = useEvolveGet('shop/facility/:facilityId');
  /**
   * Ensures the given `facilityId` is visible in the global filters.
   * (meaning: either selected, or no facilities selected).
   *
   * Throws an error if unable to ensure (for example, if the user does not have access to the facility)
   */
  const ensureFacilityVisible = useCallback(
    async (
      facilityId: FacilityId,
      /** This function will automatically check if it's
       * being called from within a `FacilityRequired`,
       * but it's possible that the caller knows it *will*
       * be used for a `FacilityRequired` but isn't inside of one yet.
       * This param can be used to tell this function to behave as if
       * we are inside of a `FacilityRequired`.
       */
      facilityRequiredOverride = facilityRequired,
    ) => {
      if (globalFilters.facilities.some((f) => f.facilityId === facilityId)) {
        if (globalFilters.facilities.length > 1 && facilityRequiredOverride) {
          updateGlobalFilters({
            facilities: globalFilters.facilities.filter((f) => f.facilityId === facilityId),
          });
        }
        return;
      }
      const facility = await getFacilityApiCall({ url: `shop/facility/${facilityId}` });
      // If no facilities are selected, but we can access the given facility,
      // keep the filter selection as-is
      if (globalFilters.facilities.length === 0 && !facilityRequiredOverride) return;
      updateGlobalFilters({
        facilities: facilityRequiredOverride ? [facility] : [...globalFilters.facilities, facility],
      });
    },
    [facilityRequired, getFacilityApiCall, globalFilters.facilities, updateGlobalFilters],
  );

  const projectRequired = isNotNil(useProjectOptional());
  const { apiCall: getProjectApiCall } = useEvolveGet('shop/project/:projectId');
  /**
   * Ensures the given `projectId` is visible in the global filters.
   * (meaning: either selected, or no projects selected).
   *
   * Throws an error if unable to ensure (for example, if the user does not have access to the project)
   */
  const ensureProjectVisible = useCallback(
    async (
      projectId: ProjectId,
      /** This function will automatically check if it's
       * being called from within a `ProjectRequired`,
       * but it's possible that the caller knows it *will*
       * be used for a `ProjectRequired` but isn't inside of one yet.
       * This param can be used to tell this function to behave as if
       * we are inside of a `ProjectRequired`.
       */
      projectRequiredOverride = projectRequired,
    ) => {
      if (globalFilters.projects.some((p) => p.projectId === projectId)) {
        if (globalFilters.projects.length > 1 && projectRequiredOverride) {
          updateGlobalFilters({ projects: globalFilters.projects.filter((p) => p.projectId === projectId) });
        }
        return;
      }
      const project = await getProjectApiCall({ url: `shop/project/${projectId}` });
      // If no projects are selected, but we can access the given project,
      // keep the filter selection as-is
      if (globalFilters.projects.length === 0 && !projectRequiredOverride) return;
      updateGlobalFilters({
        projects: projectRequiredOverride ? [project] : [...globalFilters.projects, project],
      });
    },
    [getProjectApiCall, globalFilters.projects, projectRequired, updateGlobalFilters],
  );

  return {
    ...rest,
    globalFilters,
    updateGlobalFilters,
    ensureFacilityVisible,
    ensureProjectVisible,
  };
};
